import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import ReCAPTCHA from 'react-google-recaptcha'
import { fetchWithToken } from '../../services/fetchWithToken';
import './BookingModal.css';

// Bot detection constants
const MIN_FORM_FILL_TIME = 500; // minimum time to fill the form

const BookingModal = ({ yogaClass, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentUser, bookClass } = useAuth();
  const recaptcha = useRef()
  
  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    quantity: 1,
    specialRequests: '',
  });
  
  // Honeypot field - Should remain empty
  const [honeypot, setHoneypot] = useState('');
  
  // Timer for bot detection
  const formStartTime = useRef(Date.now());
  
  // Friend booking state
  const [bookForFriend, setBookForFriend] = useState(false);
  const [friendFormData, setFriendFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialRequests: '',
  });
  
  // UI state
  const [showSummary, setShowSummary] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [friendEmailError, setFriendEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingEmail, setValidatingEmail] = useState(false);

  // Prefill form data if user is logged in
  useEffect(() => {
    if (currentUser) {
      setFormData(fd => ({
        ...fd,
        name: currentUser.name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || '',
      }));
    }
    
    // Reset the form start time when the component mounts
    formStartTime.current = Date.now();
  }, [currentUser]);

  const handleChange = e => {
    const { name, value } = e.target;
    
    // If it's an email field, reset any email errors
    if (name === 'email') {
      setEmailError('');
    }
    
    setFormData(fd => ({
      ...fd,
      [name]: name === 'quantity' ? Math.max(1, parseInt(value) || 1) : value,
    }));
  };
  
  const handleHoneypotChange = e => {
    // Track honeypot field changes
    setHoneypot(e.target.value);
  };
  
  const handleFriendChange = e => {
    const { name, value } = e.target;
    
    // If it's an email field, reset any email errors
    if (name === 'email') {
      setFriendEmailError('');
    }
    
    setFriendFormData(fd => ({
      ...fd,
      [name]: value,
    }));
  };

  // Validate email for disposable domains and MX records
  const validateEmailField = async (email, isFriend = false) => {
    if (!email || !email.includes('@')) {
      return false;
    }
    
    setValidatingEmail(true);
    
    try {
      // Call our backend API for email validation using fetchWithToken
      const response = await fetchWithToken('/api/bookings/validate-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const result = await response.json();
      
      if (!result.is_valid) {
        let errorMsg = t('invalid_email');
        
        if (result.errors.includes('DISPOSABLE_EMAIL')) {
          errorMsg = t('disposable_email_not_allowed');
        } else if (result.errors.includes('NO_MX_RECORDS')) {
          errorMsg = t('domain_has_no_mail_server');
        }
        
        if (isFriend) {
          setFriendEmailError(errorMsg);
        } else {
          setEmailError(errorMsg);
        }
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Email validation error:', error);
      return true; // Fall back to allowing the email if our validation API fails
    } finally {
      setValidatingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if the form was filled too quickly (bot detection)
    const formFillTime = Date.now() - formStartTime.current;
    if (formFillTime < MIN_FORM_FILL_TIME) {
      console.log(`Form filled too quickly (${formFillTime}ms), likely a bot`);
      setError(t('please_try_again_later'));
      return;
    }
    
    // Check if honeypot field was filled (bot detection)
    if (honeypot) {
      console.log('Honeypot field was filled, likely a bot');
      setError(t('please_try_again_later'));
      return;
    }

    // Form validation for guests
    if (!currentUser) {
      if (!formData.name.trim())    return setError(t('name_required'));
      if (!formData.email.trim())   return setError(t('email_required'));
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
                                     return setError(t('invalid_email'));
      if (!formData.phone.trim())   return setError(t('phone_required'));
      
      // Advanced email validation for guests
      const emailValid = await validateEmailField(formData.email);
      if (!emailValid) return;
    }
    
    // If booking for a friend, validate friend form data
    if (bookForFriend) {
      if (!friendFormData.name.trim())    return setError(t('friend_name_required'));
      if (!friendFormData.email.trim())   return setError(t('friend_email_required'));
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(friendFormData.email))
                                     return setError(t('friend_invalid_email'));
      if (!friendFormData.phone.trim())   return setError(t('friend_phone_required'));
      
      // Advanced email validation for friend
      const friendEmailValid = await validateEmailField(friendFormData.email, true);
      if (!friendEmailValid) return;
    }

    setError('');
    setShowSummary(true);
  };

  const handleBookingConfirmation = async () => {
    setLoading(true);
    setError('');
    
    const captchaValue = recaptcha.current.getValue()
    if (!captchaValue) {
      setError(t('recaptcha_not_ready'));
      setLoading(false);
      return;
    }

    try {
      // Prepare booking data first with correct property names (camelCase)
      const bookingData = {
        classId: yogaClass.id,
        quantity: formData.quantity,
        specialRequests: formData.specialRequests,
        captchaValue: captchaValue,
      };

      // Add guest user info if not logged in
      if (!currentUser) {
        bookingData.name = formData.name;
        bookingData.email = formData.email;
        bookingData.phone = formData.phone;
      } else {
        bookingData.userId = currentUser.id;
      }
      
      // Add friend booking data if applicable
      if (bookForFriend) {
        bookingData.bookForFriend = true;
        bookingData.friend = {
          name: friendFormData.name,
          email: friendFormData.email,
          phone: friendFormData.phone,
          specialRequests: friendFormData.specialRequests
        };
      }

      // Submit the booking
      await submitBooking(bookingData);
    } catch (err) {
      console.error('Booking error:', err);
      setLoading(false);
      setShowSummary(false);

      if (err.response?.data?.error?.code === 'INACTIVE_INSTRUCTOR') {
        setError(t('class_not_bookable'));
      } else if (err.response?.data?.error?.code === 'RECAPTCHA_FAILED') {
        setError(t('recaptcha_verification_failed'));
      } else {
        setError(err.message || t('booking_error'));
      }
    }
  };

  // Separate function to submit booking
  const submitBooking = async (bookingData) => {
    try {
      console.log('Sending booking data:', bookingData);
      const response = await bookClass(bookingData);
      setLoading(false);
      onSuccess(response);
    } catch (err) {
      console.error('Booking submission error:', err);
      setLoading(false);
      setShowSummary(false);

      if (err.response?.data?.error?.code === 'INACTIVE_INSTRUCTOR') {
        setError(t('class_not_bookable'));
      } else {
        setError(err.message || t('booking_error'));
      }
    }
  };

  const formatDate = dateTimeStr => {
    if (!dateTimeStr) return '';
    
    try {
      const date = new Date(dateTimeStr);
      const locale = t('date_locale') || 'en-US';
      
      // Use the proper locale from translations to get date and time in the correct language
      return date.toLocaleString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateTimeStr; // Return original string as fallback
    }
  };

  const modalContent = (
    <div className="booking-modal-overlay">
      <div className="booking-modal">
        <button className="close-button" onClick={onClose}>×</button>

        <h2>{showSummary ? t('booking_summary') : t('book_class')}</h2>
        <h3>{yogaClass.class_name || yogaClass.title}</h3>
        <p className="class-datetime">
          {formatDate(yogaClass.date_time || yogaClass.start_time)}
        </p>
        <p className="class-location">
          {t('class_location')} {yogaClass.location}
        </p>

        {error && <p className="error-message">{error}</p>}

        {!showSummary ? (
          <form onSubmit={handleSubmit} className="booking-form">
            {!currentUser && (
              <div className="guest-info">
                <h4>{t('guest_information')}</h4>
                <div className="form-group">
                  <label htmlFor="name">{t('name')} *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">{t('email')} *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  {emailError && <p className="field-error">{emailError}</p>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">{t('phone')} *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}

            {/* Honeypot field - Hidden from real users but bots will fill it */}
            <div className="form-group" style={{ opacity: 0, position: 'absolute', top: '-9999px', left: '-9999px' }}>
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={honeypot}
                onChange={handleHoneypotChange}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            <div className="booking-details">
              <h4>{t('booking_details')}</h4>
              <div className="form-group">
                <label htmlFor="quantity">{t('number_of_spots')}</label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  min="1"
                  max="1"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                />
                <p className="spots-info">
                  {yogaClass.available_spots} {t('spots_available')}
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="specialRequests">
                  {t('special_requests')}
                </label>
                <textarea
                  id="specialRequests"
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleChange}
                  rows="3"
                />
              </div>
              
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={bookForFriend}
                    onChange={() => setBookForFriend(!bookForFriend)}
                  />
                  {t('book_for_friend')}
                </label>
              </div>
            </div>
            
            {bookForFriend && (
              <div className="friend-info">
                <h4>{t('friend_information')}</h4>
                <div className="form-group">
                  <label htmlFor="friendName">{t('name')} *</label>
                  <input
                    type="text"
                    id="friendName"
                    name="name"
                    value={friendFormData.name}
                    onChange={handleFriendChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="friendEmail">{t('email')} *</label>
                  <input
                    type="email"
                    id="friendEmail"
                    name="email"
                    value={friendFormData.email}
                    onChange={handleFriendChange}
                    required
                  />
                  {friendEmailError && <p className="field-error">{friendEmailError}</p>}
                </div>

                <div className="form-group">
                  <label htmlFor="friendPhone">{t('phone')} *</label>
                  <input
                    type="tel"
                    id="friendPhone"
                    name="phone"
                    value={friendFormData.phone}
                    onChange={handleFriendChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="friendSpecialRequests">
                    {t('special_requests')}
                  </label>
                  <textarea
                    id="friendSpecialRequests"
                    name="specialRequests"
                    value={friendFormData.specialRequests}
                    onChange={handleFriendChange}
                    rows="3"
                  />
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={onClose}>
                {t('cancel')}
              </button>
              <button 
                type="submit" 
                className="submit-button"
                disabled={validatingEmail}
              >
                {validatingEmail ? t('verifying_email') : t('continue_to_summary')}
              </button>
            </div>
          </form>
        ) : (
          <div className="booking-summary">
            <div className="summary-details">
              <div className="summary-section">
                <h4>{t('booking_information')}</h4>
                {formData.specialRequests && (
                  <p>
                    <strong>{t('special_requests')}:</strong> {formData.specialRequests}
                  </p>
                )}
              </div>

              <div className="summary-section">
                <h4>{t('your_information')}</h4>
                <p><strong>{t('name')}:</strong> {formData.name}</p>
                <p><strong>{t('email')}:</strong> {formData.email}</p>
                <p><strong>{t('phone')}:</strong> {formData.phone}</p>
              </div>
              
              {bookForFriend && (
                <div className="summary-section">
                  <h4>{t('friend_information')}</h4>
                  <p><strong>{t('name')}:</strong> {friendFormData.name}</p>
                  <p><strong>{t('email')}:</strong> {friendFormData.email}</p>
                  <p><strong>{t('phone')}:</strong> {friendFormData.phone}</p>
                  {friendFormData.specialRequests && (
                    <p>
                      <strong>{t('special_requests')}:</strong> {friendFormData.specialRequests}
                    </p>
                  )}
                </div>
              )}
            </div>
            <ReCAPTCHA ref={recaptcha} sitekey="6LdfTT8rAAAAAM_TkD9dKutCyy3LJ4KHyINaRWtI" />
            <div className="summary-actions">
              <button
                type="button"
                className="back-button"
                onClick={() => setShowSummary(false)}
              >
                {t('back_to_form')}
              </button>
              <button
                type="button"
                className="confirm-button"
                onClick={handleBookingConfirmation}
                disabled={loading}
              >
                {loading ? t('processing') : t('confirm_booking')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    modalContent,
    document.getElementById('popup-root')
  );
};

export default BookingModal;
